/**
 * PathwayVaultPage.jsx — iter904 (Feb 2026)
 *
 * Patient's central pathway store + Personal Intervention Plan generator.
 * Single source of truth for all downstream modules.
 */
import MedsSummaryStrip from "@/components/meds/MedsSummaryStrip";
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Sparkles, RefreshCw, Vault, CalendarDays,
  Pill, Apple, Brain, CheckCircle2, TrendingUp,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import EducationalRibbon from "@/components/EducationalRibbon";
import CompoundDetailPopover from "@/components/CompoundDetailPopover";  // iter913 — GDPR-safe compound reference

const PRIORITY_COLORS = {
  critical: "from-rose-600 to-red-700",
  high:     "from-amber-500 to-orange-600",
  medium:   "from-yellow-500 to-amber-500",
  low:      "from-slate-500 to-slate-600",
};

const PRIORITY_LABEL = { critical: "Критичен", high: "Висок", medium: "Среден", low: "Нисък" };

export default function PathwayVaultPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [vault, setVault] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planLoading, setPlanLoading] = useState(false);
  const [weeks, setWeeks] = useState(4);
  const [tab, setTab] = useState("vault");
  // iter913 — GDPR compound reference popover
  const [openCompound, setOpenCompound] = useState(null);      // string | null
  const [gdprAck, setGdprAck] = useState(() => {
    try { return localStorage.getItem("nutramilo_plan_gdpr_ack") === "1"; }
    catch { return false; }
  });
  const acknowledgeGdpr = () => {
    try { localStorage.setItem("nutramilo_plan_gdpr_ack", "1"); } catch { /* ignore */ }
    setGdprAck(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pathway-vault/mine`);
      setVault(data.vault);
    } catch { setVault(null); }
    setLoading(false);
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const rebuild = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/pathway-vault/rebuild`);
      setVault(data.vault);
    } catch { /* toast */ }
    setLoading(false);
  };

  const loadPlan = useCallback(async () => {
    setPlanLoading(true);
    try {
      const { data } = await axios.get(`${API}/pathway-vault/plan?weeks=${weeks}`);
      setPlan(data);
    } catch { setPlan(null); }
    setPlanLoading(false);
  }, [API, weeks]);

  useEffect(() => { if (tab === "plan") loadPlan(); }, [tab, loadPlan]);

  const bg = isDark ? "bg-[#0a0a12] text-gray-100" : "bg-slate-50 text-gray-900";
  const cardCls = isDark ? "border-slate-700 bg-slate-900/50" : "border-slate-200 bg-white";

  return (
    <div className={`min-h-screen ${bg}`} data-testid="pathway-vault-page">
      <SEO title="Pathway Vault — Nutramilo" description="Твоите активни KEGG пътища + персонализиран план." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <div className="mb-4">
          <MedsSummaryStrip isDark={isDark} />
        </div>
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Назад
        </button>

        <EducationalRibbon lang="bg" isDark={isDark} variant="banner" className="mb-4" />

        <div className="flex items-start gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-violet-600 grid place-items-center">
            <Vault className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-extrabold">Pathway Vault</h1>
            <p className={`text-xs sm:text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              Централен склад на всички твои активни KEGG пътища. Единна точка на истина за Mind & Body, Nutrition, добавки и персонализиран план.
            </p>
          </div>
          <button onClick={rebuild} disabled={loading}
            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white inline-flex items-center gap-1"
            data-testid="vault-rebuild">
            {loading ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>} Rebuild
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          <button onClick={() => setTab("vault")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
              tab === "vault"
                ? "bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white"
                : isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
            }`} data-testid="tab-vault">
            <Vault className="inline w-3 h-3 mr-1"/> Vault
          </button>
          <button onClick={() => setTab("plan")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
              tab === "plan"
                ? "bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white"
                : isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
            }`} data-testid="tab-plan">
            <CalendarDays className="inline w-3 h-3 mr-1"/> Personal Plan
          </button>
        </div>

        {loading && <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin opacity-40"/></div>}

        {!loading && tab === "vault" && vault && (
          <div className="space-y-4" data-testid="vault-content">
            <div className={`rounded-2xl border p-4 grid grid-cols-4 gap-3 ${cardCls}`}>
              <Stat label="Състояния" value={vault.condition_count || 0}/>
              <Stat label="Активни пътища" value={vault.pathway_count || 0}/>
              <Stat label="Общ weight" value={vault.total_weight_score || 0}/>
              <Stat label="Ъпдейт" value={vault.generated_at ? new Date(vault.generated_at).toLocaleDateString("bg") : "—"} small/>
            </div>

            <div className={`rounded-2xl border p-4 ${cardCls}`}>
              <h3 className="font-bold text-sm mb-3">Активни KEGG пътища (сортирани по приоритет)</h3>
              <div className="space-y-2">
                {(vault.active_pathways || []).map(p => (
                  <div key={p.pathway_id} className={`rounded-lg p-2.5 border ${isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"}`}
                    data-testid={`pw-${p.pathway_id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <div className="text-xs font-bold">{p.name_bg}</div>
                        <code className="text-[10px] opacity-60">{p.pathway_id} · {p.kegg || "—"}</code>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] opacity-60">×{p.weight}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full text-white bg-gradient-to-r ${PRIORITY_COLORS[p.priority] || PRIORITY_COLORS.low}`}>
                          {PRIORITY_LABEL[p.priority] || p.priority}
                        </span>
                      </div>
                    </div>
                    {p.driving_conditions && p.driving_conditions.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {p.driving_conditions.map(c => {
                          // iter905 — c is now {condition_id, source} (was str)
                          const cid   = typeof c === "string" ? c : c?.condition_id;
                          const src   = typeof c === "string" ? "user_confirmed" : (c?.source || "user_confirmed");
                          const isLab = src === "auto_from_lab";
                          const cls   = isLab
                            ? (isDark ? "bg-amber-500/25 text-amber-200" : "bg-amber-100 text-amber-800 border border-amber-200")
                            : (isDark ? "bg-slate-700 text-slate-300" : "bg-white text-slate-600 border border-slate-200");
                          return (
                            <span
                              key={cid}
                              title={isLab ? "Изведено от лаборатория" : "Потвърдено състояние"}
                              className={`text-[9px] px-1.5 py-0.5 rounded ${cls}`}
                            >
                              {cid}{isLab ? " ⚠︎" : ""}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
                {(!vault.active_pathways || vault.active_pathways.length === 0) && (
                  <div className="text-xs opacity-60 text-center py-6">
                    Няма активни пътища. Добавете състояния или качете лаборатория.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {!loading && tab === "plan" && (
          <div className="space-y-4" data-testid="plan-content">
            {/* iter913 — Persistent educational banner + optional dose reveal */}
            <div
              className={`rounded-2xl border p-3 flex items-start gap-3
                ${isDark ? "bg-amber-500/15 border-amber-500/30 text-amber-100"
                         : "bg-amber-50 border-amber-200 text-amber-900"}`}
              data-testid="plan-gdpr-banner"
            >
              <div className="text-lg leading-none">⚕️</div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold">
                  Референтен план · не е медицинско предписание
                </div>
                <div className="text-[11px] mt-0.5 opacity-90">
                  Всяко съединение и доза изисква лична консултация с лекар.
                  Кликни на името за референции (HMDB / KEGG / ChEBI / PubChem).
                </div>
              </div>
              {!gdprAck ? (
                <button
                  onClick={acknowledgeGdpr}
                  data-testid="plan-gdpr-ack"
                  className="text-[10.5px] font-semibold px-2.5 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white whitespace-nowrap"
                >
                  Разбирам · покажи дози
                </button>
              ) : (
                <span
                  data-testid="plan-gdpr-acknowledged"
                  className="text-[10.5px] font-semibold whitespace-nowrap opacity-70"
                >
                  ✓ потвърдено
                </span>
              )}
            </div>

            <div className={`rounded-2xl border p-4 ${cardCls} flex items-center justify-between flex-wrap gap-2`}>
              <div>
                <h3 className="font-bold text-sm">Продължителност</h3>
                <p className="text-xs opacity-60">Персонализиран план за {weeks} седмици</p>
              </div>
              <div className="flex gap-1">
                {[2, 4, 6, 8, 12].map(w => (
                  <button key={w} onClick={() => setWeeks(w)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                      weeks === w
                        ? "bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white"
                        : isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
                    }`} data-testid={`weeks-${w}`}>{w}с</button>
                ))}
              </div>
            </div>

            {planLoading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin opacity-40"/></div>}

            {!planLoading && plan && (
              <>
                <div className={`rounded-2xl border p-4 ${cardCls}`}>
                  <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-emerald-500"/>
                    Очаквани промени след {plan.duration_weeks} седмици
                  </h3>
                  <ul className="space-y-1 text-xs">
                    {(plan.expected_markers || []).map((m, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0"/>{m}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                  <div className={`rounded-2xl border p-3 ${cardCls}`} data-testid="plan-foods">
                    <h4 className="font-bold text-xs mb-2 flex items-center gap-1"><Apple className="w-3 h-3 text-emerald-500"/>Храни ({plan.top_foods.length})</h4>
                    {plan.top_foods.map((f, i) => (
                      <div key={i} className="text-[11px] py-1 border-b border-slate-500/10 last:border-0">
                        <b>{f.name_bg}</b> — {f.compound}
                      </div>
                    ))}
                  </div>
                  <div className={`rounded-2xl border p-3 ${cardCls}`} data-testid="plan-compounds">
                    {plan.med_overlay && (
                      <div className={`mb-2 p-2 rounded-lg text-[10px] leading-snug ${
                        isDark ? "bg-white/[0.05]" : "bg-slate-50"}`}
                        data-testid="plan-med-overlay">
                        {plan.med_overlay.note_bg}
                        {!!plan.med_overlay.already_taking.length && (
                          <div className="mt-0.5">
                            Вече приемате: {plan.med_overlay.already_taking.join(", ")}
                          </div>
                        )}
                        {!!plan.med_overlay.with_warning.length && (
                          <div className="mt-0.5 font-semibold">
                            С предупреждение: {plan.med_overlay.with_warning.join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                    <h4 className="font-bold text-xs mb-2 flex items-center gap-1"><Pill className="w-3 h-3 text-violet-500"/>Съединения ({plan.top_compounds.length})</h4>
                    {plan.top_compounds.map((c, i) => {
                      const tm = c.twin_match || {};
                      const total = tm.total || 0;
                      const active = tm.active || 0;
                      const pct = total ? Math.round((active / total) * 100) : 0;
                      const matchColor = pct >= 40
                        ? (isDark ? "text-emerald-300" : "text-emerald-700")
                        : pct >= 20
                        ? (isDark ? "text-amber-300"   : "text-amber-700")
                        : (isDark ? "text-slate-400"   : "text-slate-500");
                      return (
                        <div key={i} className="text-[11px] py-1.5 border-b border-slate-500/10 last:border-0"
                          data-testid={`plan-compound-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <button
                              type="button"
                              onClick={() => setOpenCompound(c.name_bg)}
                              className={`text-left flex-1 min-w-0 truncate hover:underline ${isDark ? "text-violet-300" : "text-violet-700"}`}
                              data-testid={`plan-compound-open-${i}`}
                              title="Виж референции + консултирай лекар"
                            >
                              <b>{c.name_bg}</b>
                            </button>
                            {gdprAck ? (
                              <span className="text-[10px] opacity-70 whitespace-nowrap">
                                {c.dose_bg}
                              </span>
                            ) : (
                              <span className={`text-[9.5px] px-1.5 py-0.5 rounded-full whitespace-nowrap
                                ${isDark ? "bg-amber-500/20 text-amber-200" : "bg-amber-100 text-amber-800"}`}>
                                обсъди с лекар
                              </span>
                            )}
                          </div>
                          {/* iter1018 — сверено с лекарствата и добавките на пациента */}
                          {c.already_taking && (
                            <div className={`mt-1 text-[10px] font-semibold ${
                              isDark ? "text-emerald-300" : "text-emerald-700"}`}
                              data-testid={`plan-compound-taking-${i}`}>
                              {c.taking_note_bg}
                            </div>
                          )}
                          {(c.interactions_bg || []).map((w, wi) => (
                            <div key={wi}
                              className={`mt-1 text-[10px] font-semibold ${
                                isDark ? "text-amber-300" : "text-amber-700"}`}
                              data-testid={`plan-compound-warning-${i}`}>
                              ⚠ {w}
                            </div>
                          ))}
                          {/* iter915 — Twin match indicator */}
                          {total > 0 ? (
                            <div
                              className="mt-1 flex items-center gap-1.5"
                              data-testid={`plan-compound-twin-${i}`}
                              title={`${active} от ${total} CTD-свързаните пътища за този compound са активни при теб`}
                            >
                              <div className={`h-1 rounded-full flex-1 overflow-hidden ${isDark ? "bg-slate-700/60" : "bg-slate-200"}`}>
                                <div
                                  className="h-full bg-gradient-to-r from-emerald-500 to-sky-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className={`text-[9.5px] font-mono whitespace-nowrap ${matchColor}`}>
                                <b>{active}</b>/{total} пътя · {pct}%
                              </span>
                            </div>
                          ) : (
                            <div className={`mt-0.5 text-[9.5px] ${isDark ? "text-slate-500" : "text-slate-400"}`}>
                              Няма CTD данни за twin match
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className={`rounded-2xl border p-3 ${cardCls}`} data-testid="plan-mindbody">
                    <h4 className="font-bold text-xs mb-2 flex items-center gap-1"><Brain className="w-3 h-3 text-pink-500"/>Mind-body ({plan.top_mind_body.length})</h4>
                    {plan.top_mind_body.map((m, i) => (
                      <div key={i} className="text-[11px] py-1 border-b border-slate-500/10 last:border-0">
                        <b>{m.name_bg}</b> · {m.weekly_dose_bg}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  {(plan.weeks || []).map(w => (
                    <div key={w.week} className={`rounded-2xl border p-4 ${cardCls}`} data-testid={`week-${w.week}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-sm">Седмица {w.week} · {w.focus_bg}</h4>
                        <span className="text-[10px] opacity-60">Compliance цел: {w.compliance_target_pct}%</span>
                      </div>
                      <div className="space-y-1">
                        {(w.checklist || []).slice(0, 10).map((item, i) => {
                          const isCompound = item.type === "compound";
                          // iter913 — extract dose from BG string ("Berberine — 500 mg 3×/ден")
                          const parts = (item.text || "").split(/[-–—]/);
                          const compoundName = (parts[0] || item.text || "").trim();
                          const doseText = parts.slice(1).join("—").trim();
                          return (
                            <div key={i} className="flex items-start gap-2 text-[11px]">
                              <input type="checkbox" className="mt-0.5"/>
                              <div className="flex-1 min-w-0">
                                {isCompound ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => setOpenCompound(compoundName)}
                                      className={`text-left hover:underline font-bold ${isDark ? "text-violet-300" : "text-violet-700"}`}
                                      data-testid={`week-${w.week}-compound-${i}`}
                                    >
                                      {compoundName}
                                    </button>
                                    {gdprAck && doseText && (
                                      <span className="opacity-70"> · {doseText}</span>
                                    )}
                                    {!gdprAck && (
                                      <span className={`ml-1 text-[9.5px] px-1.5 py-0.5 rounded-full
                                        ${isDark ? "bg-amber-500/20 text-amber-200" : "bg-amber-100 text-amber-800"}`}>
                                        обсъди с лекар
                                      </span>
                                    )}
                                    {item.when && <span className="opacity-60"> · {item.when}</span>}
                                  </>
                                ) : (
                                  <>
                                    <b className="opacity-70">[{item.type}]</b> {item.text}
                                    {item.when && <span className="opacity-60"> · {item.when}</span>}
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className={`rounded-2xl border p-3 text-xs opacity-80 ${cardCls}`}>
                  <b>Забележка:</b> {plan.disclaimer}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* iter913 — GDPR-safe compound reference popover */}
      <CompoundDetailPopover
        open={!!openCompound}
        query={openCompound}
        onClose={() => setOpenCompound(null)}
        isDark={isDark}
      />
    </div>
  );
}

function Stat({ label, value, small }) {
  return (
    <div className="rounded-lg bg-white/40 dark:bg-white/5 p-2 text-center">
      <div className="text-[9px] uppercase opacity-60">{label}</div>
      <div className={`font-bold ${small ? "text-xs" : "text-xl"}`}>{value}</div>
    </div>
  );
}
